import { OnChange } from '@/features/form/types'
import { PrintType } from '@/features/print/constants'
import {
    FormKeys,
    defaultFormValues,
} from '@/features/settings/components/PrinterConfiguration/form/constants'
import { schema } from '@/features/settings/components/PrinterConfiguration/form/schema'
import { Form, OnFormSubmit } from '@/features/settings/components/PrinterConfiguration/form/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import {
    UseFormReturn,
    useController,
    useFormContext,
    useForm as useRHFForm,
} from 'react-hook-form'

const getDefaultValues = (initialValues: Partial<Form>) => {
    return {
        ...defaultFormValues,
        ...initialValues,
    }
}

export const useForm = (initialValues: Partial<Form>): UseFormReturn<Form> => {
    const methods = useRHFForm<Form>({
        defaultValues: getDefaultValues(initialValues),
        resolver: zodResolver(schema),
        mode: 'onChange',
    })

    const { reset } = methods

    useEffect(() => {
        reset(getDefaultValues(initialValues))
    }, [initialValues, reset])

    return methods
}

export const useNameField = (): [string, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<Form, FormKeys.Name>({ name: FormKeys.Name })

    return [value, onChange]
}

export const useCharsPerLineField = (): [string, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<Form, FormKeys.CharsPerLine>({ name: FormKeys.CharsPerLine })

    return [value, onChange]
}

export const usePaperWidthField = (): [string, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<Form, FormKeys.PaperWidth>({ name: FormKeys.PaperWidth })

    return [value, onChange]
}

export const usePrintTypesField = (): [PrintType[], OnChange] => {
    const {
        field: { value, onChange },
    } = useController<Form, FormKeys.PrintTypes>({ name: FormKeys.PrintTypes })

    return [value, onChange]
}
export const useFormSubmit = (
    onFormSubmit: OnFormSubmit
): [{ isValid: boolean; isSubmitting: boolean }, () => void] => {
    const {
        handleSubmit,
        formState: { isSubmitting, isValid },
    } = useFormContext<Form>()

    const handleValid = async (data: Form) => {
        await onFormSubmit(data)
    }

    const submitHandler = handleSubmit(handleValid)

    return [{ isValid, isSubmitting }, submitHandler]
}
